import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { TIME_OPTIONS, WeeklyHours } from "@/data/goals";
import Navigation from "@/components/Navigation";

export default function OnboardingTime() {
  const navigate = useNavigate();
  const [selectedHours, setSelectedHours] = useState<WeeklyHours | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing onboarding data
  useEffect(() => {
    const loadOnboarding = async () => {
      try {
        const onboarding = await api.getOnboarding();
        if (onboarding?.weekly_hours) {
          setSelectedHours(onboarding.weekly_hours);
        }
      } catch (err) {
        // Ignore errors, just use default
      }
    };
    loadOnboarding();
  }, []);

  const handleContinue = async () => {
    if (!selectedHours) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get existing goal from onboarding
      const onboarding = await api.getOnboarding();
      if (!onboarding?.goal) {
        navigate("/onboarding/goals");
        return;
      }

      // Save with both goal and hours
      await api.saveOnboarding({
        goal: onboarding.goal,
        weekly_hours: selectedHours,
      });

      // Navigate to assessment
      navigate("/assessment");
    } catch (err: any) {
      setError(err.message || "Failed to save preferences");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4">
        <div className="max-w-xl w-full space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-indigo-100 dark:bg-indigo-900/50">
              <Clock className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            How much time can you commit?
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Consistent practice is key. Choose what works for your schedule.
          </p>
        </div>

        {/* Time Options */}
        <div className="space-y-3">
          {TIME_OPTIONS.map((option) => {
            const isSelected = selectedHours === option.hours;

            return (
              <Card
                key={option.hours}
                className={`cursor-pointer transition-all duration-200 border-2 ${
                  isSelected
                    ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
                onClick={() => setSelectedHours(option.hours)}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected
                          ? "border-indigo-500 bg-indigo-500"
                          : "border-gray-400 dark:border-gray-500"
                      }`}
                    >
                      {isSelected && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-semibold ${
                            isSelected
                              ? "text-gray-900 dark:text-white"
                              : "text-gray-700 dark:text-gray-200"
                          }`}
                        >
                          {option.label}
                        </span>
                        {option.recommended && (
                          <span className="flex items-center gap-1 text-xs bg-indigo-600 text-white px-2 py-0.5 rounded-full">
                            <Sparkles className="w-3 h-3" />
                            Recommended
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {option.dailyMinutes}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`text-sm ${
                      isSelected
                        ? "text-indigo-600 dark:text-indigo-300"
                        : "text-gray-500 dark:text-gray-500"
                    }`}
                  >
                    {option.description}
                  </span>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Card */}
        <Card className="bg-blue-50 dark:bg-gray-800 border-blue-200 dark:border-gray-700">
          <CardContent className="p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                Pro tip:
              </span>{" "}
              Even 10-15 minutes of focused daily practice can lead to
              significant improvement. Start small and build consistency.
            </p>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="text-center text-red-500 text-sm">{error}</div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/onboarding/goals")}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <Button
            size="lg"
            disabled={!selectedHours || isLoading}
            onClick={handleContinue}
            className="px-8 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {isLoading ? (
              "Saving..."
            ) : (
              <>
                Start Assessment
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center gap-2">
          <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
          <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
          <div className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600"></div>
        </div>
        </div>
      </div>
    </>
  );
}
